import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { systemSettingsApi } from '@/features/admin/services/systemSettingsApi';

const DEFAULT_CURRENCY = 'KES';
const DEFAULT_LOCALE = 'en-KE';

const PUBLIC_SETTINGS_QUERY_KEY = ['system-settings', 'public'] as const;

interface CurrencyContext {
  currency: string;
  locale: string;
  format: (amount: number, options?: Intl.NumberFormatOptions) => string;
}

/**
 * Reads the system-wide currency + locale settings and returns a memoized formatter.
 * Falls back to KES / en-KE while the settings are loading or if the request fails,
 * so call sites never have to handle a "no currency yet" state.
 */
export const useCurrency = (): CurrencyContext => {
  const { data } = useQuery({
    queryKey: PUBLIC_SETTINGS_QUERY_KEY,
    queryFn: () => systemSettingsApi.getPublic(),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const currency =
      data?.find((s) => s.key === 'Localization.Currency')?.value ?? DEFAULT_CURRENCY;
    const locale =
      data?.find((s) => s.key === 'Localization.CurrencyLocale')?.value ?? DEFAULT_LOCALE;

    const format = (amount: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        ...options,
      }).format(amount);

    return { currency, locale, format };
  }, [data]);
};
